import React, { useState, useEffect } from 'react';
import { Plus, FileText, Users, Clock, Archive } from 'lucide-react';
import { questionnaireService } from '../services/questionnaireService';
import CreateQuestionnaireModal from './CreateQuestionnaireModal';
import EditQuestionnaireModal from './EditQuestionnaireModal';
import QuestionnaireActionsMenu from './QuestionnaireActionsMenu';
import QuestionnaireAssignmentsView from './QuestionnaireAssignmentsView';
import QuestionnaireOverview from './QuestionnaireOverview';
import type { Questionnaire } from '../types/questionnaires';

const ScrollitellingDashboard: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingQuestionnaire, setEditingQuestionnaire] = useState<Questionnaire | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedForAssignments, setSelectedForAssignments] = useState<Questionnaire | null>(null);
  const [selectedForOverview, setSelectedForOverview] = useState<Questionnaire | null>(null);

  useEffect(() => {
    loadQuestionnaires();
  }, []);

  const loadQuestionnaires = async () => {
    try {
      setIsLoading(true);
      const data = await questionnaireService.listQuestionnaires();
      setQuestionnaires(data);
    } catch (error) {
      console.error('Error loading questionnaires:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (q: Questionnaire) => {
    setEditingQuestionnaire(q);
  };

  const handleDuplicate = async (q: Questionnaire) => {
    const confirmed = window.confirm(
      `Möchten Sie den Fragebogen "${q.title}" duplizieren?\n\n` +
      `Eine Kopie wird erstellt.`
    );
    
    if (!confirmed) return;
    
    try {
      await questionnaireService.duplicateQuestionnaire(q.id);
      alert('Fragebogen erfolgreich dupliziert!');
      loadQuestionnaires();
    } catch (error) {
      console.error('Error duplicating:', error);
      alert('Fehler beim Duplizieren');
    }
  };

  const handleArchive = async (q: Questionnaire) => {
    const action = q.archived ? 'wiederherstellen' : 'archivieren';
    const confirmed = window.confirm(
      `Möchten Sie den Fragebogen "${q.title}" ${action}?`
    );
    
    if (!confirmed) return;
    
    try {
      if (q.archived) {
        await questionnaireService.restoreQuestionnaire(q.id);
        alert('Fragebogen wiederhergestellt!');
      } else {
        await questionnaireService.archiveQuestionnaire(q.id);
        alert('Fragebogen archiviert!');
      }
      loadQuestionnaires();
    } catch (error) {
      console.error('Error archiving:', error);
      alert('Fehler beim Archivieren');
    }
  };

  const handleDelete = async (q: Questionnaire) => {
    const confirmed = window.confirm(
      `WARNUNG: Möchten Sie den Fragebogen "${q.title}" wirklich PERMANENT löschen?\n\n` +
      `Diese Aktion kann NICHT rückgängig gemacht werden.\n` +
      `Alle Zuweisungen und Antworten bleiben erhalten, aber der Fragebogen selbst wird gelöscht.`
    );
    
    if (!confirmed) return;
    
    // Double confirmation for destructive action
    const doubleConfirm = window.confirm(
      `Letzte Bestätigung: "${q.title}" wirklich löschen?`
    );
    
    if (!doubleConfirm) return;
    
    try {
      await questionnaireService.deleteQuestionnaire(q.id);
      alert('Fragebogen gelöscht');
      loadQuestionnaires();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Fehler beim Löschen');
    }
  };

  // Filter archived questionnaires
  const filteredQuestionnaires = questionnaires.filter(q => 
    showArchived ? true : !q.archived
  );

  const archivedCount = questionnaires.filter(q => q.archived).length;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Fragebögen
          </h1>
          <p className="text-gray-600">
            Erstellen und verwalten Sie Fragebögen für verschiedene Projekte
          </p>
        </div>
        <div className="flex items-center gap-4">
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Archive className="w-4 h-4" />
              {showArchived ? 'Aktive anzeigen' : `Archivierte anzeigen (${archivedCount})`}
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-purple-700 transition-colors font-semibold shadow-md"
          >
            <Plus className="w-5 h-5" />
            Neuer Fragebogen
          </button>
        </div>
      </div>

      {questionnaires.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Noch keine Fragebögen
          </h2>
          <p className="text-gray-600 mb-6">
            Erstellen Sie Ihren ersten Fragebogen, um Projektinformationen von Teammitgliedern zu sammeln.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Jetzt erstellen
          </button>
        </div>
      ) : (
        <div className="grid gap-6">
          {filteredQuestionnaires.map(q => (
            <div
              key={q.id}
              onClick={() => setSelectedForOverview(q)}
              className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <FileText className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-xl font-bold text-gray-900">{q.title}</h3>
                      {q.archived && (
                        <span className="ml-3 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Archive className="w-3 h-3" />
                          Archiviert
                        </span>
                      )}
                    </div>
                    
                    {q.description && (
                      <p className="text-gray-600 mb-4">{q.description}</p>
                    )}
                    
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>
                          Erstellt: {new Date(q.createdAt).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        <span>
                          Modus: {q.mode === 'quick' ? 'Schnellerfassung' : 'Vollständig'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-xs text-gray-500 italic">
                      💡 Klicken Sie auf die Karte, um alle Antworten zu sehen
                    </div>
                  </div>
                  
                  <div className="ml-4 flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedForAssignments(q);
                      }}
                      className="px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium"
                    >
                      Zuweisungen
                    </button>
                    
                    <QuestionnaireActionsMenu
                      questionnaire={q}
                      onEdit={() => handleEdit(q)}
                      onDuplicate={() => handleDuplicate(q)}
                      onArchive={() => handleArchive(q)}
                      onDelete={() => handleDelete(q)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {showCreateModal && (
        <CreateQuestionnaireModal 
          onClose={() => setShowCreateModal(false)}
          onCreated={loadQuestionnaires}
        />
      )}
      
      {editingQuestionnaire && (
        <EditQuestionnaireModal
          questionnaire={editingQuestionnaire}
          onClose={() => setEditingQuestionnaire(null)}
          onSaved={() => {
            setEditingQuestionnaire(null);
            loadQuestionnaires();
          }}
        />
      )}
      
      {selectedForAssignments && (
        <QuestionnaireAssignmentsView 
          questionnaire={selectedForAssignments}
          onClose={() => setSelectedForAssignments(null)}
        />
      )}
      
      {selectedForOverview && (
        <QuestionnaireOverview 
          questionnaire={selectedForOverview}
          onClose={() => setSelectedForOverview(null)}
        />
      )}
    </div>
  );
};

export default ScrollitellingDashboard;

